const PREFIX = 'panews-data-cache:v1:'
const memory = new Map<string, CacheRecord<unknown>>()
const inflight = new Map<string, Promise<unknown>>()

interface CacheRecord<T> { value:T; storedAt:number }
export interface CachedValue<T> { value:T; storedAt:number; age:number; fresh:boolean }

function storage() {
  try { return typeof localStorage === 'undefined' ? null : localStorage } catch { return null }
}

export function readCached<T>(key:string, freshFor:number, maxAge=6*60*60*1000):CachedValue<T>|null {
  let record=memory.get(key) as CacheRecord<T>|undefined
  if(!record){
    const store=storage()
    try{
      const raw=store?.getItem(PREFIX+key)
      if(raw)record=JSON.parse(raw) as CacheRecord<T>
    }catch{try{store?.removeItem(PREFIX+key)}catch{/* storage unavailable */}}
    if(record)memory.set(key,record)
  }
  if(!record||typeof record.storedAt!=='number')return null
  const age=Math.max(0,Date.now()-record.storedAt)
  if(age>maxAge)return null
  return {value:record.value,storedAt:record.storedAt,age,fresh:age<=freshFor}
}

export function writeCached<T>(key:string,value:T,storedAt=Date.now()){
  const record:CacheRecord<T>={value,storedAt}
  memory.set(key,record)
  try{storage()?.setItem(PREFIX+key,JSON.stringify(record))}catch{/* memory cache remains available */}
  return record
}

export function refreshCached<T>(key:string,loader:()=>Promise<T>):Promise<T>{
  const running=inflight.get(key) as Promise<T>|undefined
  if(running)return running
  const request=loader().then(value=>{writeCached(key,value);return value}).finally(()=>inflight.delete(key))
  inflight.set(key,request)
  return request
}

export async function runLimited(tasks:Array<()=>Promise<unknown>>,concurrency=2){
  const queue=[...tasks]
  async function worker(){for(;;){const task=queue.shift();if(!task)return;try{await task()}catch{/* individual feed keeps its cached fallback */}}}
  await Promise.all(Array.from({length:Math.min(Math.max(1,concurrency),tasks.length)},worker))
}

export function clearDataCacheForTests(){memory.clear();inflight.clear()}
